import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useHistory, useLocation, useRouteMatch } from 'react-router-dom';

import {
  btag,
  Bus,
  constants,
  pushToDataLayer,
  SessionStorageManager,
  SiteConfigManager,
  useApplicationState,
  useDetection,
  usePaymentMethods,
} from '@atlas/core';
import { clientProfileRoutes, COMPONENT_TYPES, paymentRoutes } from '@atlas/routing';
import { EmptyStateView, HeaderTitle, Icon, Label, Loader } from '@atlas/ui';

import { DATA_LAYER_STORAGE_KEYS } from 'common/constants';
import DepositPaymentPage from 'components/ClientProfile/Payment/Deposit/DepositPaymentPage';
import SeoPageText from 'components/SeoPage/SeoPageText';
import StaticBlock from 'src/common/StaticBlock/StaticBlock';
import useDepositExitPopup from 'src/hooks/useDepositExitPopup';

import { DepositBonusProvider } from './DepositBonusContext';
import DepositListItem from './DepositListItem';

const { CLIENT_PROFILE } = COMPONENT_TYPES;

const { payment } = clientProfileRoutes;

const { deposit } = paymentRoutes;

const DepositList = defaultValue => {
  const preselectedPaymentMethod = SiteConfigManager.getConfig(
    'config.config.mobile.preselected_payment_method',
  );
  const clientProfilePath = SiteConfigManager.getComponentPath(CLIENT_PROFILE);
  const {
    params: { paymentMethod: paymentMethodId },
    url,
  } = useRouteMatch();
  const history = useHistory();
  const { search } = useLocation();
  const [paymentUrl, setPaymentUrl] = useState();
  const [paymentMethod, setPaymentMethod] = useState(null);
  const { browser, ip, locale, os } = useDetection();
  const { isLoading, paymentMethods, updatePaymentMethods } = usePaymentMethods(
    constants.PAYMENT_OPERATION.DEPOSIT,
    constants.DEVICE_TYPES.MOBILE,
  );

  const isCashboxOpened = SessionStorageManager.getItem(DATA_LAYER_STORAGE_KEYS.CASHBOX_OPENED);
  const { setHasSuccessfulDepositInSession } = useDepositExitPopup({
    allowedPathPrefix: `${clientProfilePath}/${payment}/${deposit}`,
    showPopupOnAction: false,
  });

  const { account } = useApplicationState();

  useEffect(() => {
    if (!account.id || isCashboxOpened) return;

    const sendCashboxOpenAnalytics = () => {
      pushToDataLayer(
        'cashbox_open',
        {
          OS: os,
          browser,
          created_at: Date.now(),
          device: constants.DEVICE_TYPES.MOBILE,
          ip,
          locale,
          page_referer: window.location.pathname,
          user_id: account.id,
        },
        btag,
      );
      SessionStorageManager.setItem(DATA_LAYER_STORAGE_KEYS.CASHBOX_OPENED, true);
    };

    sendCashboxOpenAnalytics();
  }, [account.id, browser, ip, locale, os, isCashboxOpened]);

  useEffect(() => {
    if (!paymentMethods.length) {
      setPaymentMethod(null);
      return;
    }

    if (!paymentMethodId) {
      setPaymentMethod(null);

      if (preselectedPaymentMethod) {
        history.replace(`${clientProfilePath}/${payment}/${deposit}/${paymentMethods[0].id}`);
      }

      return;
    }

    const paymentMethod = paymentMethods.find(method => method.id === Number(paymentMethodId));

    setPaymentMethod(paymentMethod || null);

    if (!paymentMethod) {
      history.push(`${clientProfilePath}/${payment}/${deposit}`);
    }
  }, [clientProfilePath, history, paymentMethodId, paymentMethods, preselectedPaymentMethod]);

  const handleDepositComplete = useCallback(() => {
    updatePaymentMethods();
    setHasSuccessfulDepositInSession();
  }, [updatePaymentMethods, setHasSuccessfulDepositInSession]);

  useEffect(() => {
    Bus.on(Bus.events.profile.depositComplete, handleDepositComplete);

    return () => Bus.off(Bus.events.profile.depositComplete, handleDepositComplete);
  }, [handleDepositComplete]);

  const renderBackButton = useMemo(() => {
    return preselectedPaymentMethod ? (
      <Link className="back-to-container" to={clientProfilePath}>
        <Icon className="icon-back" name="back" />
        <Label className="title-text" message="deposit" />
      </Link>
    ) : null;
  }, [preselectedPaymentMethod, clientProfilePath]);

  if (isLoading) {
    return <Loader className="relative" />;
  }

  if (!paymentMethods) {
    return (
      <>
        {renderBackButton}
        <div className="payments-list empty">
          <EmptyStateView description="deposit_empty_message" title="deposit_empty_title" />
        </div>
        <SeoPageText />
      </>
    );
  }

  return (
    <>
      <DepositBonusProvider>
        {paymentMethods.length === 0 ? (
          (() => {
            const staticBlockConfig = SiteConfigManager.getConfig(
              'config.config.useStaticBlocksFromConfig',
              defaultValue,
            );

            return (
              <>
                {renderBackButton}
                {staticBlockConfig ? (
                  <StaticBlock
                    fallback={
                      <EmptyStateView
                        className="viewport-height"
                        description="deposit_empty_message"
                        title="deposit_empty_title"
                      />
                    }
                    options={{ source: 'deposit-empty-info-mobile' }}
                  />
                ) : (
                  <EmptyStateView
                    className="viewport-height"
                    description="deposit_empty_message"
                    title="deposit_empty_title"
                  />
                )}
              </>
            );
          })()
        ) : (
          <>
            {!preselectedPaymentMethod && (
              <>
                <div className="title-wrapper bottom-space">
                  <HeaderTitle className="sub-text block-medium" title="select_payment_system" />
                </div>
                {paymentMethods.map(payment => {
                  const { id } = payment;

                  return (
                    <DepositListItem
                      key={id}
                      methodAsLink
                      payment={payment}
                      to={`${url}/${id}${search}`}
                    />
                  );
                })}
              </>
            )}
            <DepositPaymentPage
              paymentMethod={paymentMethod}
              paymentMethods={paymentMethods}
              paymentUrl={paymentUrl}
              setPaymentMethod={setPaymentMethod}
              setPaymentUrl={setPaymentUrl}
            />
          </>
        )}
      </DepositBonusProvider>
      <SeoPageText />
    </>
  );
};

export default DepositList;
